import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign, Plus, FolderPlus, Trash2, Layers, ArrowUp, ArrowDown } from "lucide-react";
import LineItemCard from "./LineItemCard";
import { groupLineItemsBySections } from "@/utils/lineItemSections";
import { calculateAcres, calculateTotalAcres } from "../utils/mapCalculations";

const LineItemsSection = ({
  lineItems,
  services,
  polygons = [],
  onLinkArea,
  onAddLineItem,
  onAddSection,
  onUpdateLineItem,
  onRemoveLineItem,
  onMoveSection,
  onMoveItem,
}) => {
  const groups = useMemo(() => groupLineItemsBySections(lineItems), [lineItems]);
  const hasAnySection = groups.some((g) => g.header);
  const headerGroupCount = useMemo(() => groups.filter((g) => g.header).length, [groups]);

  const areaOptions = useMemo(() => {
    if (!polygons || polygons.length === 0) return [];
    const opts = polygons.map((p, i) => ({
      value: p._id,
      label: `${p._name || `Area ${i + 1}`} — ${calculateAcres(p).toFixed(2)} ac`,
    }));
    if (polygons.length > 1) {
      opts.push({ value: "__all__", label: `All areas — ${calculateTotalAcres(polygons).toFixed(2)} ac total` });
    }
    return opts;
  }, [polygons]);

  return (
    <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
      <CardHeader className="border-b border-zinc-100 px-6 py-4">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d] flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Services & Pricing
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onAddSection}
              data-testid="add-section-btn"
              className="h-10 text-[#00341d] border-[#00341d]/30 hover:bg-[#00341d]/5"
            >
              <FolderPlus className="w-4 h-4 mr-1.5" /> Add Section
            </Button>
            <Select onValueChange={(v) => onAddLineItem(v, null)}>
              <SelectTrigger className="w-56 h-10" data-testid="add-service-select">
                <SelectValue placeholder="+ Add Service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
                ))}
                <SelectItem value="custom" className="border-t border-zinc-200 mt-1 pt-1">
                  <span className="flex items-center gap-2 text-[#d97706] font-medium">
                    <Plus className="w-4 h-4" />Custom Line Item
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {lineItems.length === 0 ? (
          <div className="text-center py-12 text-zinc-400" data-testid="line-items-empty">
            <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No services added yet</p>
            <p className="text-xs mt-1">Use <span className="font-medium">+ Add Service</span> or <span className="font-medium">+ Add Section</span> to start.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map((group, gIdx) => {
              const headerOrdinal = groups.slice(0, gIdx).filter((g) => g.header).length;
              return (
                <SectionGroup
                  key={group.header ? group.header.id : `ungrouped-${gIdx}`}
                  group={group}
                  services={services}
                  areaOptions={areaOptions}
                  onLinkArea={onLinkArea}
                  onAddLineItem={onAddLineItem}
                  onUpdateLineItem={onUpdateLineItem}
                  onRemoveLineItem={onRemoveLineItem}
                  onMoveSection={onMoveSection}
                  onMoveItem={onMoveItem}
                  canMoveSectionUp={!!group.header && headerOrdinal > 0}
                  canMoveSectionDown={!!group.header && headerOrdinal < headerGroupCount - 1}
                  showSubtotal={hasAnySection}
                  flatIndexBase={getFlatIndexBase(groups, gIdx)}
                />
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function getFlatIndexBase(groups, gIdx) {
  let base = 0;
  for (let i = 0; i < gIdx; i++) {
    if (groups[i].header) base += 1;
    base += groups[i].items.length;
  }
  if (groups[gIdx]?.header) base += 1; // skip the header itself
  return base;
}

const SectionGroup = ({
  group,
  services,
  areaOptions,
  onLinkArea,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  onMoveSection,
  onMoveItem,
  canMoveSectionUp,
  canMoveSectionDown,
  showSubtotal,
  flatIndexBase,
}) => {
  const { header, items, subtotal } = group;
  const isUngrouped = !header;
  const sectionHeaderId = header?.id || null;

  return (
    <div
      className={
        isUngrouped
          ? "space-y-4"
          : "border border-[#00341d]/20 rounded-md bg-[#00341d]/[0.02]"
      }
      data-testid={header ? `section-group-${header.id}` : "section-group-ungrouped"}
    >
      {header && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[#00341d]/5 border-b border-[#00341d]/15 rounded-t-md">
          <Layers className="w-4 h-4 text-[#00341d] flex-shrink-0" />
          <Input
            value={header.service_name || ""}
            onChange={(e) => onUpdateLineItem(header.id, "service_name", e.target.value)}
            placeholder="Section title"
            className="font-heading uppercase tracking-tight text-[#00341d] bg-transparent border-none h-8 px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
            data-testid={`section-title-input-${header.id}`}
          />
          <Select onValueChange={(v) => onAddLineItem(v, sectionHeaderId)}>
            <SelectTrigger
              className="w-44 h-8 bg-white"
              data-testid={`section-add-service-${header.id}`}
            >
              <SelectValue placeholder="+ Add to section" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service.id} value={service.id}>{service.name}</SelectItem>
              ))}
              <SelectItem value="custom" className="border-t border-zinc-200 mt-1 pt-1">
                <span className="flex items-center gap-2 text-[#d97706] font-medium">
                  <Plus className="w-4 h-4" />Custom Line Item
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMoveSection?.(header.id, "up")}
            disabled={!canMoveSectionUp}
            className="h-8 w-8 p-0 text-[#00341d] disabled:opacity-30"
            data-testid={`section-move-up-${header.id}`}
            title="Move section up"
          >
            <ArrowUp className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMoveSection?.(header.id, "down")}
            disabled={!canMoveSectionDown}
            className="h-8 w-8 p-0 text-[#00341d] disabled:opacity-30"
            data-testid={`section-move-down-${header.id}`}
            title="Move section down"
          >
            <ArrowDown className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemoveLineItem(header.id)}
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
            data-testid={`section-remove-${header.id}`}
            title="Remove section (items stay, will become ungrouped)"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}

      {header && (
        <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-[#00341d]/[0.02] border-b border-[#00341d]/10" data-testid={`section-controls-${header.id}`}>
          <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
            <input
              type="checkbox"
              checked={!!header.optional}
              onChange={(e) => {
                onUpdateLineItem(header.id, "optional", e.target.checked);
                if (e.target.checked && header.accepted === undefined) onUpdateLineItem(header.id, "accepted", true);
              }}
              className="rounded border-amber-400"
              data-testid={`section-optional-${header.id}`}
            />
            <span className="text-amber-600 font-medium">Optional section (customer can decline the whole section)</span>
          </label>
          {header.optional && (
            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input
                type="checkbox"
                checked={header.accepted !== false}
                onChange={(e) => onUpdateLineItem(header.id, "accepted", e.target.checked)}
                className="rounded border-zinc-300"
                data-testid={`section-accepted-${header.id}`}
              />
              <span>Selected by default (customer can uncheck)</span>
            </label>
          )}
          <label className="flex items-center gap-2 text-xs text-zinc-600">
            <span className="whitespace-nowrap">Section lump sum $</span>
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="itemized"
              value={header.section_lump_sum ?? ""}
              onChange={(e) => onUpdateLineItem(header.id, "section_lump_sum", e.target.value === "" ? null : (parseFloat(e.target.value) || 0))}
              className="w-28 h-8 px-2 border border-zinc-200 rounded bg-white"
              data-testid={`section-lump-sum-${header.id}`}
            />
          </label>
        </div>
      )}

      <div className={header ? "p-4 space-y-4" : "space-y-4"}>
        {items.length === 0 ? (
          <div className="text-center py-4 text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md">
            No items in this section yet. Use <span className="font-medium">+ Add to section</span> above.
          </div>
        ) : (
          items.map((item, idx) => (
            <LineItemCard
              key={item.id}
              item={item}
              index={flatIndexBase + idx}
              areaOptions={areaOptions}
              onLinkArea={onLinkArea}
              onUpdate={onUpdateLineItem}
              onRemove={onRemoveLineItem}
              onMove={onMoveItem}
              canMoveUp={idx > 0}
              canMoveDown={idx < items.length - 1}
            />
          ))
        )}

        {header && showSubtotal && (
          <div className="flex justify-end pt-2 border-t border-[#00341d]/10">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-zinc-600">Section subtotal</span>
              <span
                className="font-bold text-[#00341d]"
                data-testid={`section-subtotal-${header.id}`}
              >
                ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LineItemsSection;
